import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AUDIT_TRAIL_PAGE_SIZE } from '../constants/acknowledgementAuditTrail.constants';
import type { AuditTrailSortField } from '../types/acknowledgementAuditTrail.types';

export interface AuditTrailFilterState {
  facilityId: number | null;
  startDate: string;
  endDate: string;
  criticalityBand: string;
  acknowledgementStatus: string;
  clinician: string;
  search: string;
}

export interface AuditTrailListState {
  page: number;
  sortBy: AuditTrailSortField;
  sortOrder: 'ASC' | 'DESC';
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthStartIsoDate(): string {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
}

function parseIntParam(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export function useAcknowledgementAuditTrailFilters(defaultFacilityId: number | null) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<AuditTrailFilterState>(() => {
    const facilityParam = searchParams.get('facilityId');
    const facilityId =
      facilityParam != null && facilityParam !== '' ? Number(facilityParam) : defaultFacilityId;

    return {
      facilityId: Number.isInteger(facilityId) && facilityId > 0 ? facilityId : null,
      startDate: searchParams.get('startDate') || monthStartIsoDate(),
      endDate: searchParams.get('endDate') || todayIsoDate(),
      criticalityBand: searchParams.get('criticalityBand') || '',
      acknowledgementStatus: searchParams.get('acknowledgementStatus') || '',
      clinician: searchParams.get('clinician') || '',
      search: searchParams.get('search') || '',
    };
  }, [searchParams, defaultFacilityId]);

  const listState = useMemo<AuditTrailListState>(() => {
    const sortBy = (searchParams.get('sortBy') || 'createdAt') as AuditTrailSortField;
    const sortOrder =
      (searchParams.get('sortOrder') || 'DESC').toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
    return {
      page: parseIntParam(searchParams.get('page'), 1),
      sortBy,
      sortOrder,
    };
  }, [searchParams]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>, { resetPage = false } = {}) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value == null || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        if (resetPage) {
          next.delete('page');
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const updateFilters = useCallback(
    (patch: Partial<AuditTrailFilterState>, options?: { resetPage?: boolean }) => {
      const updates: Record<string, string | null> = {};
      if (patch.facilityId !== undefined) {
        updates.facilityId = patch.facilityId != null ? String(patch.facilityId) : null;
      }
      if (patch.startDate !== undefined) {
        updates.startDate = patch.startDate || null;
      }
      if (patch.endDate !== undefined) {
        updates.endDate = patch.endDate || null;
      }
      if (patch.criticalityBand !== undefined) {
        updates.criticalityBand = patch.criticalityBand || null;
      }
      if (patch.acknowledgementStatus !== undefined) {
        updates.acknowledgementStatus = patch.acknowledgementStatus || null;
      }
      if (patch.clinician !== undefined) {
        updates.clinician = patch.clinician || null;
      }
      if (patch.search !== undefined) {
        updates.search = patch.search || null;
      }
      updateSearchParams(updates, options);
    },
    [updateSearchParams]
  );

  const updateListState = useCallback(
    (patch: Partial<AuditTrailListState>) => {
      const updates: Record<string, string | null> = {};
      if (patch.page !== undefined) {
        updates.page = patch.page > 1 ? String(patch.page) : null;
      }
      if (patch.sortBy !== undefined) {
        updates.sortBy = patch.sortBy;
      }
      if (patch.sortOrder !== undefined) {
        updates.sortOrder = patch.sortOrder;
      }
      updateSearchParams(updates);
    },
    [updateSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams();
      const facilityId =
        prev.get('facilityId') || (defaultFacilityId ? String(defaultFacilityId) : '');
      if (facilityId) {
        next.set('facilityId', facilityId);
      }
      next.set('startDate', monthStartIsoDate());
      next.set('endDate', todayIsoDate());
      return next;
    });
  }, [defaultFacilityId, setSearchParams]);

  const apiFilters = useMemo(() => {
    if (!filters.facilityId) {
      return null;
    }
    return {
      facilityId: filters.facilityId,
      startDate: filters.startDate ? `${filters.startDate}T00:00:00.000Z` : undefined,
      endDate: filters.endDate ? `${filters.endDate}T23:59:59.999Z` : undefined,
      criticalityBand: filters.criticalityBand || undefined,
      acknowledgementStatus: filters.acknowledgementStatus || undefined,
      clinician: filters.clinician.trim() || undefined,
      search: filters.search.trim() || undefined,
      limit: AUDIT_TRAIL_PAGE_SIZE,
      offset: (listState.page - 1) * AUDIT_TRAIL_PAGE_SIZE,
      sortBy: listState.sortBy,
      sortOrder: listState.sortOrder,
    };
  }, [filters, listState]);

  return {
    filters,
    listState,
    apiFilters,
    pageSize: AUDIT_TRAIL_PAGE_SIZE,
    updateFilters,
    updateListState,
    clearFilters,
  };
}
